import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { api, eur, formatApiError } from "@/lib/api";
import { refreshShippingConfig } from "@/lib/shipping";
import { PeriodPicker, PERIODS } from "@/components/admin/AdminRevenue";

const num = "h-9 w-24 rounded-lg border border-black/15 bg-white px-2 font-mono text-sm";
const card = "rounded-2xl border border-black/10 bg-white p-5";
const two = (v) => (v === null || v === undefined || v === "" ? "" : Number(v).toFixed(2));
const fmt = (c) => ({ ...c, italy_fee: two(c.italy_fee), eu_fee: two(c.eu_fee), cod_fee: two(c.cod_fee),
  italy_promos: c.italy_promos.map((p) => ({ ...p, min: two(p.min) })), eu_promos: c.eu_promos.map((p) => ({ ...p, min: two(p.min) })),
  tiers: c.tiers.map((t) => ({ ...t, cost: two(t.cost) })) });

const Promos = ({ zone, label, list, onChange }) => (
  <div className="space-y-2" data-testid={`promos-${zone}`}>
    <p className="text-sm font-semibold">Promozioni {label}</p>
    {list.map((p, i) => (
      <div key={i} className="flex flex-wrap items-center gap-2 text-sm">
        <span>Ordini da</span><input type="number" min="0" step="0.01" value={p.min} onChange={(e) => onChange(list.map((x, j) => (j === i ? { ...x, min: e.target.value } : x)))} className={num} data-testid={`promo-${zone}-min-${i}`} />
        <span>€ → sconto</span><input type="number" min="1" max="100" value={p.pct} onChange={(e) => onChange(list.map((x, j) => (j === i ? { ...x, pct: e.target.value } : x)))} className={`${num} w-20`} data-testid={`promo-${zone}-pct-${i}`} />
        <span>% {Number(p.pct) === 100 && <b className="text-emerald-700">(gratis)</b>}</span>
        <button type="button" onClick={() => onChange(list.filter((_, j) => j !== i))} className="p-1 text-rose-600" data-testid={`promo-${zone}-remove-${i}`}><Trash2 className="h-4 w-4" /></button>
      </div>
    ))}
    <button type="button" onClick={() => onChange([...list, { min: "", pct: 100 }])} className="flex items-center gap-1 text-xs font-semibold text-cyan-700 underline" data-testid={`promo-${zone}-add`}><Plus className="h-3.5 w-3.5" />Aggiungi promozione</button>
  </div>
);

const ShippingSettings = ({ onSaved }) => {
  const [c, setC] = useState(null);
  useEffect(() => { api.get("/admin/shipping-config").then((r) => setC(fmt(r.data))); }, []);
  if (!c) return null;
  const set = (k) => (e) => setC({ ...c, [k]: e.target.value });
  const setTier = (i, k, v) => setC({ ...c, tiers: c.tiers.map((t, j) => (j === i ? { ...t, [k]: v } : t)) });
  const save = async (e) => {
    e.preventDefault();
    const f = (v) => (v === "" || v === null ? null : Number(v));
    const promos = (l) => l.map((p) => ({ min: Number(p.min || 0), pct: parseInt(p.pct, 10) }));
    const body = { italy_fee: f(c.italy_fee), eu_fee: f(c.eu_fee), cod_fee: f(c.cod_fee), italy_promos: promos(c.italy_promos), eu_promos: promos(c.eu_promos),
      tiers: c.tiers.map((t) => ({ name: t.name, max_weight_g: f(t.max_weight_g), max_volume_cm3: f(t.max_volume_cm3), cost: f(t.cost) })) };
    try { const { data } = await api.put("/admin/shipping-config", body); setC(fmt(data)); refreshShippingConfig(); toast.success("Tariffe di spedizione salvate"); onSaved(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <form onSubmit={save} className={`${card} min-w-0 space-y-5 overflow-x-auto`} data-testid="shipping-settings">
      <div>
        <p className="font-display text-lg font-bold">Prezzi pagati dai clienti</p>
        <div className="mt-3 flex flex-wrap gap-4 text-sm">
          {[["italy_fee", "Corriere Italia (€)"], ["eu_fee", "Corriere UE (€)"], ["cod_fee", "Supplemento contrassegno (€)"]].map(([k, l]) => (
            <label key={k} className="space-y-1"><span className="block text-xs text-stone-600">{l}</span><input required type="number" min="0" step="0.01" value={c[k]} onChange={set(k)} className={num} data-testid={`ship-${k}`} /></label>
          ))}
        </div>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <Promos zone="italy" label="Italia" list={c.italy_promos} onChange={(l) => setC({ ...c, italy_promos: l })} />
        <Promos zone="eu" label="UE" list={c.eu_promos} onChange={(l) => setC({ ...c, eu_promos: l })} />
      </div>
      <p className="text-xs text-stone-500">Vale la promozione con la soglia più alta raggiunta dal carrello. Con 100% la spedizione è gratuita.</p>
      <div>
        <p className="font-display text-lg font-bold">Costi del corriere (per il margine)</p>
        <p className="text-xs text-stone-500">Il pacco va nella prima fascia in cui entrano peso e volume. L'ultima fascia vale per tutto il resto (lascia vuoti i limiti).</p>
        <div className="mt-2 space-y-2">
          {c.tiers.map((t, i) => (
            <div key={i} className="flex flex-wrap items-center gap-2 text-sm" data-testid={`tier-${i}`}>
              <input required value={t.name} onChange={(e) => setTier(i, "name", e.target.value)} className={`${num} w-36 font-sans`} data-testid={`tier-name-${i}`} />
              <span>fino a</span><input type="number" min="1" value={t.max_weight_g ?? ""} onChange={(e) => setTier(i, "max_weight_g", e.target.value)} placeholder="∞" className={num} data-testid={`tier-weight-${i}`} /><span>g e</span>
              <input type="number" min="1" value={t.max_volume_cm3 ?? ""} onChange={(e) => setTier(i, "max_volume_cm3", e.target.value)} placeholder="∞" className={num} data-testid={`tier-volume-${i}`} /><span>cm³ · costo</span>
              <input required type="number" min="0" step="0.01" value={t.cost} onChange={(e) => setTier(i, "cost", e.target.value)} className={num} data-testid={`tier-cost-${i}`} /><span>€</span>
              {c.tiers.length > 1 && <button type="button" onClick={() => setC({ ...c, tiers: c.tiers.filter((_, j) => j !== i) })} className="p-1 text-rose-600" data-testid={`tier-remove-${i}`}><Trash2 className="h-4 w-4" /></button>}
            </div>
          ))}
          <button type="button" onClick={() => setC({ ...c, tiers: [...c.tiers.slice(0, -1), { name: "Nuova fascia", max_weight_g: "", max_volume_cm3: "", cost: "" }, c.tiers[c.tiers.length - 1]] })} className="flex items-center gap-1 text-xs font-semibold text-cyan-700 underline" data-testid="tier-add"><Plus className="h-3.5 w-3.5" />Aggiungi fascia</button>
        </div>
      </div>
      <button className="h-11 w-full rounded-full bg-amber-500 font-bold text-black" data-testid="shipping-settings-save">Salva tariffe</button>
    </form>
  );
};

const RealCost = ({ o, onSaved }) => {
  const [v, setV] = useState(o.real_cost ?? "");
  const save = async () => {
    try { await api.put(`/admin/orders/${o.id}/courier-cost`, { real_cost: v === "" ? null : Number(v) }); toast.success("Costo aggiornato"); onSaved(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <div className="flex items-center gap-1.5">
      <input type="number" min="0" step="0.01" value={v} onChange={(e) => setV(e.target.value)} placeholder={String(o.estimated_cost)} className={`${num} w-20`} data-testid={`margin-real-${o.id}`} />
      <button onClick={save} disabled={String(v) === String(o.real_cost ?? "")} className="rounded-full bg-black px-2.5 py-1 text-xs font-bold text-white disabled:opacity-30" data-testid={`margin-save-${o.id}`}>OK</button>
    </div>
  );
};

export const AdminShippingMargin = ({ onChange }) => {
  const [range, setRange] = useState(PERIODS.mese());
  const [d, setD] = useState(null);
  const load = () => api.get("/admin/shipping-margin", { params: { start: range[0], end: range[1] } }).then((r) => setD(r.data));
  useEffect(() => { load(); }, [range]); // eslint-disable-line react-hooks/exhaustive-deps
  const saved = () => { load(); onChange?.(); };
  return (
    <div className="space-y-5" data-testid="admin-shipping-margin">
      <div className="min-w-0 space-y-4">
        <PeriodPicker range={range} setRange={setRange} prefix="margin" />
        {d && <>
          <div className="grid gap-3 sm:grid-cols-3">
            <div className={card}><p className="text-xs uppercase text-stone-500">Pagato dai clienti</p><p className="font-mono text-xl font-bold">{eur(d.fees)}</p></div>
            <div className={card}><p className="text-xs uppercase text-stone-500">Costo corriere</p><p className="font-mono text-xl font-bold">{eur(d.costs)}</p></div>
            <div className={card}><p className="text-xs uppercase text-stone-500">Margine</p><p className={`font-mono text-xl font-bold ${d.margin < 0 ? "text-rose-600" : "text-emerald-600"}`} data-testid="margin-total">{eur(d.margin)}</p></div>
          </div>
          {d.missing_real > 0 && <p className="text-xs text-amber-700">{d.missing_real} spedizioni usano ancora il costo stimato: inserisci il costo reale pagato al corriere.</p>}
          <div className="overflow-x-auto rounded-2xl border border-black/10 bg-white">
            <table className="w-full min-w-[680px] text-sm" data-testid="margin-table">
              <thead className="text-left text-xs uppercase tracking-wider text-stone-500"><tr><th className="p-3">Ordine</th><th className="p-3">Cliente</th><th className="p-3">Fascia</th><th className="p-3">Pagato</th><th className="p-3">Costo reale</th><th className="p-3">Margine</th></tr></thead>
              <tbody>
                {d.orders.map((o) => (
                  <tr key={o.id} className="border-t border-black/[0.06]" data-testid={`margin-row-${o.id}`}>
                    <td className="p-3"><p className="font-mono text-xs">{o.order_number}</p><p className="text-xs text-stone-500">{new Date(o.created_at).toLocaleDateString("it-IT")}</p></td>
                    <td className="p-3">{o.customer}<span className="block text-xs text-stone-500">{o.country}</span></td>
                    <td className="p-3 text-xs">{o.tier}<span className="block text-stone-500">stima {eur(o.estimated_cost)}</span></td>
                    <td className="p-3 font-mono">{eur(o.fee)}</td>
                    <td className="p-3"><RealCost o={o} onSaved={saved} /></td>
                    <td className={`p-3 font-mono font-bold ${o.margin < 0 ? "text-rose-600" : "text-emerald-600"}`}>{eur(o.margin)}</td>
                  </tr>
                ))}
                {!d.orders.length && <tr><td colSpan={6} className="p-6 text-center text-stone-500">Nessuna spedizione con corriere nel periodo</td></tr>}
              </tbody>
            </table>
          </div>
        </>}
      </div>
      <ShippingSettings onSaved={saved} />
    </div>
  );
};
